import React, { useEffect, useMemo, useState } from "react";
import api, { fmtInt } from "@/api";
import { useI18n } from "@/i18n";
import { useCurrency } from "@/currency";
import { useShowPrices } from "@/showPrices";
import { PageHeader, Tile, Loading, StatusPill } from "@/components/Bits";
import KanbanBoard from "@/components/KanbanBoard";
import { GridFour, Kanban as KanbanIcon } from "@phosphor-icons/react";

const YEARS = [2025, 2026, 2027, 2028, 2029, 2030];

function GridView() {
  const { t, lang } = useI18n();
  const { fmt: fmtMoney } = useCurrency();
  const { showPrices } = useShowPrices();
  const [opps, setOpps] = useState(null);

  useEffect(() => {
    api.get("/opportunities", { params: { limit: 5000 } }).then((r) => setOpps(r.data.rows));
  }, []);

  const grid = useMemo(() => {
    if (!opps) return null;
    const byOp = {};
    let grand = 0;
    const totals = Object.fromEntries(YEARS.map((y) => [y, 0]));
    for (const o of opps) {
      const year = o.next_ovh;
      if (!YEARS.includes(year)) continue;
      const op = o.operator || "Unknown";
      const v = showPrices ? (o.active_method || 0) : (o.total_qty || 0);
      byOp[op] = byOp[op] || Object.fromEntries(YEARS.map((y) => [y, 0]));
      byOp[op][year] += v;
      totals[year] += v;
      grand += v;
    }
    const rows = Object.entries(byOp)
      .map(([op, y]) => ({ op, ...y, total: YEARS.reduce((s, yr) => s + y[yr], 0) }))
      .sort((a, b) => b.total - a.total);
    return { rows, totals, grand };
  }, [opps, showPrices]);

  if (!opps) return <Loading />;
  const maxCell = Math.max(...grid.rows.flatMap((r) => YEARS.map((y) => r[y])), 1);
  const bg = (v) => {
    if (!v) return "transparent";
    const a = Math.min(1, v / maxCell);
    return `rgba(237, 32, 39, ${0.1 + a * 0.85})`;
  };
  const cellFmt = (v) => {
    if (!v) return "—";
    if (showPrices) return fmtMoney(v);
    return fmtInt(v);
  };
  const headerLabel = showPrices
    ? `${t("pipeline.active")} • ${fmtMoney(grid.grand)}`
    : `${lang === "fr" ? "Quantité d'équipements" : "Equipment quantity"} • ${fmtInt(grid.grand)} ${lang === "fr" ? "unités" : "units"}`;

  return (
    <Tile>
      <div className="label-tech mb-3">{headerLabel}</div>
      <div className="overflow-x-auto">
        <table className="tech" data-testid="pipeline-grid">
          <thead>
            <tr>
              <th>{t("table.operator")}</th>
              {YEARS.map((y) => <th key={y} className="text-right">{y}</th>)}
              <th className="text-right">{t("pipelineGrid.total")}</th>
            </tr>
          </thead>
          <tbody>
            {grid.rows.map((r) => (
              <tr key={r.op}>
                <td>{r.op}</td>
                {YEARS.map((y) => (
                  <td key={y} className="num" style={{ background: bg(r[y]) }}>
                    {cellFmt(r[y])}
                  </td>
                ))}
                <td className="num font-medium text-wabtec-red">{cellFmt(r.total)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-white/30">
              <td className="font-medium">{t("pipelineGrid.total")}</td>
              {YEARS.map((y) => (
                <td key={y} className="num font-medium">{cellFmt(grid.totals[y])}</td>
              ))}
              <td className="num font-medium text-wabtec-red">{cellFmt(grid.grand)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Tile>
  );
}

export default function Pipeline() {
  const { t, lang } = useI18n();
  const { showPrices } = useShowPrices();
  const [view, setView] = useState(() => localStorage.getItem("pipeline_view") || "grid");

  const setMode = (m) => {
    setView(m);
    localStorage.setItem("pipeline_view", m);
  };

  const tab = (id, Icon, label) => (
    <button
      key={id}
      onClick={() => setMode(id)}
      data-testid={`pipeline-tab-${id}`}
      className="flex items-center gap-2 px-3 py-1.5 text-[13px] rounded-[10px] transition-all"
      style={{
        background: view === id ? "var(--text-primary)" : "transparent",
        color: view === id ? "white" : "var(--text-secondary)",
        border: view === id ? "none" : "1px solid var(--border)",
      }}
    >
      <Icon size={14} weight={view === id ? "fill" : "regular"} />
      {label}
    </button>
  );

  return (
    <div data-testid="pipeline-page">
      <PageHeader
        title={t("pipelineGrid.title")}
        subtitle={view === "grid"
          ? (showPrices ? t("pipelineGrid.subtitle") : (lang === "fr" ? "Quantité d'équipements par opérateur × année" : "Equipment quantity by operator × year"))
          : t("kanban.subtitle")}
        right={
          <div className="flex items-center gap-1.5">
            {tab("grid", GridFour, lang === "fr" ? "Grille annuelle" : "Yearly grid")}
            {tab("kanban", KanbanIcon, "Kanban OVH")}
          </div>
        }
      />
      <div className="p-4 md:p-8">
        {view === "grid" ? <GridView /> : <KanbanBoard StatusPill={StatusPill} />}
      </div>
    </div>
  );
}
